import { useState, useRef, useEffect, useCallback } from 'react';
import { graphicToGeoJSON, drawProjectGeometry } from '../utils/arcgisMapUtils';

/**
 * Encapsulates ProjectDetailMap's boundary drawing/selection state machine:
 * the Sketch-widget draw flow (load Sketch → draw polygon → persist → exit),
 * the selected-boundary refs the map's shared click handler mutates, and the
 * cancel/delete actions.
 *
 * The host map owns the ArcGIS view and the (display + edit) boundary layer and
 * feeds their refs in. The returned refs/setter are wired into the host's
 * shared `view.on('click')` handler (for boundary selection) and its teardown;
 * the returned state/handlers drive the overlay buttons + imperative `startDraw`.
 *
 * @param {object} args
 * @param {boolean} args.enableBoundaryEditing
 * @param {React.MutableRefObject} args.viewRef          - ArcGIS MapView ref (host-owned)
 * @param {boolean} args.mapReady
 * @param {React.MutableRefObject} args.boundaryLayerRef - boundary GraphicsLayer ref (host-owned)
 * @param {React.MutableRefObject} args.projectNameRef   - current project name ref (host-owned)
 * @param {(geojson: object) => void} args.onBoundaryDrawn
 * @param {(geometryId: number) => void} args.onBoundaryDeleted
 */
export function useBoundaryEditing({
  enableBoundaryEditing,
  viewRef,
  mapReady,
  boundaryLayerRef,
  projectNameRef,
  onBoundaryDrawn,
  onBoundaryDeleted,
}) {
  const [drawMode, setDrawMode] = useState(false);
  const [selectedBoundary, setSelectedBoundary] = useState(null);

  const sketchRef = useRef(null);
  const sketchLayerRef = useRef(null);
  const selectedBoundaryRef = useRef(null);
  const originalSymbolRef = useRef(null);
  const drawModeRef = useRef(false);
  const enableBoundaryEditingRef = useRef(enableBoundaryEditing);
  enableBoundaryEditingRef.current = enableBoundaryEditing;
  const onBoundaryDrawnRef = useRef(onBoundaryDrawn);
  onBoundaryDrawnRef.current = onBoundaryDrawn;
  const onBoundaryDeletedRef = useRef(onBoundaryDeleted);
  onBoundaryDeletedRef.current = onBoundaryDeleted;

  useEffect(() => { drawModeRef.current = drawMode; }, [drawMode]);

  // Sketch widget for drawing boundaries
  useEffect(() => {
    if (!mapReady || !viewRef.current || !drawMode) return;
    if (!window.require) return;

    window.require(
      ['esri/layers/GraphicsLayer', 'esri/widgets/Sketch'],
      (GraphicsLayer, Sketch) => {
        try {
          if (!viewRef.current) return;
          const graphicsLayer = new GraphicsLayer({ id: 'boundary-sketch-layer' });
          viewRef.current.map.add(graphicsLayer);
          sketchLayerRef.current = graphicsLayer;

          const sketch = new Sketch({
            layer: graphicsLayer,
            view: viewRef.current,
            creationMode: 'single',
            availableCreateTools: ['polygon'],
            activeCreateOptions: {
              polygonSymbol: {
                type: 'simple-fill',
                color: [230, 154, 36, 0.15],       // LPC Study Orange
                outline: { color: [230, 154, 36], width: 2.5, style: 'solid' }
              }
            }
          });

          sketch.on('create', (event) => {
            if (event.state === 'complete') {
              const geojson = graphicToGeoJSON(event.graphic);
              if (geojson) {
                // Add to boundary layer with proper styling
                drawProjectGeometry(viewRef.current, geojson, {
                  layer: boundaryLayerRef,
                  fitBounds: false,
                  title: projectNameRef.current ? `${projectNameRef.current} Boundary` : 'Survey Boundary'
                });
                if (onBoundaryDrawnRef.current) onBoundaryDrawnRef.current(geojson);
              }
              setDrawMode(false);
            }
          });

          sketchRef.current = sketch;
          sketch.create('polygon', { mode: 'click' });
        } catch (err) {
          console.error('Sketch widget init error:', err);
          setDrawMode(false);
        }
      },
      (err) => {
        console.error('Failed to load Sketch modules:', err);
        setDrawMode(false);
      }
    );

    return () => {
      if (sketchRef.current) {
        sketchRef.current.cancel();
        sketchRef.current.destroy();
        sketchRef.current = null;
      }
      // Always remove the sketch layer on cleanup (graphic was copied to boundary
      // layer). The layer is created in the async require callback, so cleanup
      // must read the view/layer from refs rather than a captured local.
      if (sketchLayerRef.current && viewRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        viewRef.current.map.remove(sketchLayerRef.current);
        sketchLayerRef.current = null;
      }
    };
  }, [mapReady, drawMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const startDraw = useCallback(() => setDrawMode(true), []);

  const handleCancelDraw = useCallback(() => {
    if (sketchRef.current) sketchRef.current.cancel();
    setDrawMode(false);
  }, []);

  const handleDeleteSelected = useCallback(() => {
    if (!selectedBoundaryRef.current) return;
    const { graphic, geometryId } = selectedBoundaryRef.current;
    if (boundaryLayerRef.current) {
      boundaryLayerRef.current.remove(graphic);
    }
    setSelectedBoundary(null);
    selectedBoundaryRef.current = null;
    if (onBoundaryDeletedRef.current && geometryId) {
      onBoundaryDeletedRef.current(geometryId);
    }
  }, [boundaryLayerRef]);

  return {
    // state for render
    drawMode,
    selectedBoundary,
    // actions
    startDraw,
    handleCancelDraw,
    handleDeleteSelected,
    // refs + setter wired into the host's shared click handler + teardown
    setSelectedBoundary,
    sketchRef,
    selectedBoundaryRef,
    originalSymbolRef,
    drawModeRef,
    enableBoundaryEditingRef,
  };
}
